import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LoadingButton } from '../../components/LoadingSpinner';
import { useToast } from '../../components/Toast';
import { adminLogin } from '../../services/api.js';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { showSuccess, showError, ToastContainer } = useToast();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showError('Please fix the errors in the form');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Pass credentials as a single object
      const response = await adminLogin({
        email: formData.email,
        password: formData.password
      });
      
      // Check if we got a demo token
      if (response.message?.includes('demo')) {
        showSuccess('Admin demo mode activated! Redirecting...');
      } else {
        showSuccess('Welcome back, Admin! Redirecting...');
      }
      
      setTimeout(() => {
        navigate('/admin/dashboard');
      }, 1000);
    } catch (error) {
      // Handle specific error cases with more helpful messages
      if (error.response?.status === 404) {
        showError('Admin API not available. Please check server connection or contact your system administrator.');
      } else if (error.response?.status === 401) {
        showError('Invalid admin credentials. Please check your email and password.');
      } else if (error.response?.status === 405) {
        showError('Authentication server error: The backend API expects a different method for admin login. Please contact your system administrator.');
      } else if (error.response?.status >= 500) {
        showError('Server error. Please try again later or contact your system administrator.');
      } else if (error.message.includes('endpoint not found')) {
        showError('Admin login service unavailable. The backend may be offline or misconfigured.');
      } else if (error.message.includes('Cannot connect')) {
        showError('Connection to admin authentication service failed. Please check your internet connection.');
      } else {
        showError(error.message || 'Login failed. Please try again.');
      }
      
      // Log the error for debugging
      console.error('Admin login error:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-900">
      <ToastContainer />
      
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Link to="/" className="flex items-center justify-center mb-2">
            <img src="/logo.svg" alt="Link Spaghetti Logo" className="h-10" />
          </Link>
          <Link to="/" className="inline-block mb-4">
            <h1 className="text-3xl font-bold text-transparent bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text">
              Link Spaghetti Admin
            </h1>
          </Link>
          
          <div className="bg-yellow-500 text-yellow-900 px-4 py-2 rounded-lg inline-block mb-6">
            <p className="text-sm font-medium flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Restricted Access - Admins Only
            </p>
          </div>
          
          <h2 className="text-3xl font-extrabold text-white">
            Admin Login
          </h2>
          <p className="mt-2 text-sm text-gray-400">
            Enter your admin credentials to continue
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="bg-gray-800 p-8 rounded-lg shadow-xl">
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                  Admin Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className={`appearance-none block w-full px-3 py-2 border ${
                    errors.email ? 'border-red-500' : 'border-gray-600'
                  } rounded-md shadow-sm bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500`}
                  placeholder="admin@linkspaghetti.com"
                  disabled={isLoading}
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red-400">{errors.email}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className={`appearance-none block w-full px-3 py-2 border ${
                    errors.password ? 'border-red-500' : 'border-gray-600'
                  } rounded-md shadow-sm bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500`}
                  placeholder="••••••••"
                  disabled={isLoading}
                />
                {errors.password && (
                  <p className="mt-1 text-xs text-red-400">{errors.password}</p>
                )}
              </div>
            </div>

            <div className="mt-6">
              <LoadingButton
                type="submit"
                loading={isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 focus:ring-offset-gray-800"
                disabled={isLoading}
              >
                Sign In as Admin
              </LoadingButton>
            </div>


          </div>
        </form>

        <div className="text-center">
          <Link to="/signin" className="text-sm text-gray-400 hover:text-gray-300">
            Not an admin? Sign in as user →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin; 